﻿/**
 * FragmentCommandManager
 * Owns post-mount commands to fragments:
 * submit (w/ duplicate-submission guarding), process, reset, set-values,
 * and dispatch() for commands described by external sub-modules.
 */

import { createLogger } from '@anddone/ajs-logger';

import type {
  CommandMessageType,
  CommandPayloadByType,
  Fragment,
  FragmentName,
  FragmentValues,
  SetValuesCommandPayload,
  InputValue,
} from '@anddone/ajs-util';
import {
  FRAGMENT_CONFIG,
  TransactionAssembleResponseMessagePayload,
  TransactionStatusMessagePayload,
  TransactionStatusEventPayload,
} from '@anddone/ajs-util';
import type { Events } from '../Events';
import type { Orchestrator } from '../Orchestrator';
import type { StateManagement } from '../StateManagement';
import type { FragmentRegistry } from '../fragment-registry/FragmentRegistry';
import type { FragmentLifecycleManager } from './FragmentLifecycleManager';
import type { CommandDispatcher, CommandTarget } from '@anddone/ajs-util';

const logger = createLogger('SDK', 'FragmentCommandManager');

export class FragmentCommandManager implements CommandDispatcher {
  /**
   * constructor
   *
   * @param dataState - Shared config and SDK state
   * @param orchestrator - Reference to orchestrator module
   * @param events - Reference to events module
   * @param fragmentRegistry - Reference to fragmentRegistry module
   * @param lifecycle - Fragment lifecycle manager
   */
  constructor(
    private readonly dataState: StateManagement,
    private readonly orchestrator: Orchestrator,
    private readonly events: Events,
    private readonly fragmentRegistry: FragmentRegistry,
    private readonly lifecycle: FragmentLifecycleManager,
  ) {}

  /**
   * Send a command to the fragment(s) described by target.
   *
   * @param target - A fragment name, or 'payment-fragments' for every payment fragment
   * @param type - Command message type
   * @param data - Command payload
   * @returns true if at least one registered fragment was targeted
   */
  public dispatch<Type extends CommandMessageType>(
    target: CommandTarget,
    type: Type,
    data: CommandPayloadByType[Type],
  ): boolean {
    const fragments = this.resolveTarget(target);

    if (fragments.length === 0) {
      logger.debug('dispatch', `Skipped "${type}" — no registered fragment for "${target}".`, {
        target,
        type,
      });
      return false;
    }

    fragments.forEach(fragment => {
      this.orchestrator.sendCommandToFragment(fragment, type, data);
    });

    return true;
  }

  /**
   * Send a log line to the orchestrator fragment.
   *
   * @param message - Log message
   * @returns void
   */
  public logToOrchestrator(message: string): void {
    this.orchestrator.sendLogCommandToOrchestratorFragment(message);
  }

  /**
   * Push the current fragment states to the orchestrator fragment.
   *
   * @returns void
   */
  public refreshOrchestratorStatus(): void {
    this.orchestrator.sendStatusUpdateCommandToOrchestratorFragment();
  }

  private resolveTarget(target: CommandTarget): Fragment[] {
    if (target === 'payment-fragments') {
      return this.fragmentRegistry
        .getFragments()
        .filter(fragment => FRAGMENT_CONFIG.get(fragment.fragmentName)?.isPaymentFragment);
    }

    const fragment =
      target === 'orchestrator'
        ? this.fragmentRegistry.getOrchestrator()
        : this.fragmentRegistry.get(target);
    return fragment ? [fragment] : [];
  }

  /**
   * Send reset command to all fragments via orchestrator.
   *
   * @returns void
   */
  public reset(): void {
    this.dataState.attempts.reset();

    const processableFragments = this.fragmentRegistry.getProcessableFragments();
    const names = processableFragments.map(([name]) => name);

    logger.info('reset-fragment', `Resetting ${names.length} fragment(s)`, names);

    processableFragments.forEach(([, fragment]) => {
      fragment.status = 'pending';
      fragment.values = undefined;
      this.orchestrator.sendCommandToFragment(fragment, 'reset-fragment', null);
    });

    this.events.emit('reset', null);
    this.orchestrator.sendStatusUpdateCommandToOrchestratorFragment();
  }

  /**
   * Send set-values command to a specific fragment via orchestrator.
   * Only sends to non-sensitive fragments.
   *
   * @returns void
   */
  public setValues(targetFragmentName: FragmentName, values: FragmentValues | undefined): void {
    if (this.dataState.attempts.isProcessing) {
      logger.warn('set-values', 'Set values command ignored — submission already in progress');
      return;
    }

    const config = FRAGMENT_CONFIG.get(targetFragmentName);

    if (!config) {
      logger.warn('set-values', `Fragment "${targetFragmentName}" not found in configuration`);
      return;
    }

    if (!values) {
      logger.warn('set-values', `Cannot update values for ${targetFragmentName}. Values not set. `);
      return;
    }

    if (!config.canSetValues) {
      logger.warn(
        'set-values',
        `Fragment "${targetFragmentName}" is not configured to allow setting values externally.`,
      );
      return;
    }

    const targetFragment = this.fragmentRegistry.get(targetFragmentName);
    logger.debug(
      'set-values',
      `Set Value request initiated for ${targetFragmentName} fragment.`,
      values,
    );
    if (!targetFragment) {
      logger.warn('set-values', `Fragment "${targetFragmentName}" not found in registry`);
      return;
    }

    logger.info('set-values', `Setting values for ${targetFragmentName} fragment`, values);
    const payload: SetValuesCommandPayload = {
      fragmentName: targetFragmentName,
      values: values,
    };
    this.orchestrator.sendCommandToFragment(targetFragment, 'set-values', payload);
  }

  /**
   * Apply an initialValue from a fragment's mount config after its handshake completes.
   *
   * @param fragmentName - Name of the fragment
   * @returns void
   */
  public applyInitialValue(fragmentName: FragmentName): void {
    const mountConfig = this.dataState.config.fragmentsToLoad.find(
      c => c.fragmentName === fragmentName,
    );

    if (!mountConfig?.initialValue) {
      return;
    }

    const { initialValue } = mountConfig;
    const config = FRAGMENT_CONFIG.get(fragmentName);

    if (!config) {
      logger.warn('initial-value', `Fragment "${fragmentName}" not found in configuration`);
      return;
    }

    let values: FragmentValues;

    if (config.inputMode === 'multiple') {
      if (
        typeof initialValue !== 'object' ||
        initialValue === null ||
        Array.isArray(initialValue)
      ) {
        logger.warn(
          'initial-value',
          `Fragment "${fragmentName}" expects a plain object for multiple-input mode`,
        );
        return;
      }
      values = new Map(Object.entries(initialValue as Record<string, InputValue>));
    } else if (config.inputMode === 'single') {
      if (typeof initialValue !== 'string') {
        logger.warn(
          'initial-value',
          `Fragment "${fragmentName}" expects a string value for single-input mode`,
        );
        return;
      }
      if (!config.input) {
        logger.warn(
          'initial-value',
          `Fragment "${fragmentName}" is missing an "input" name in its configuration`,
        );
        return;
      }
      values = new Map([[config.input, initialValue]]);
    } else {
      logger.warn(
        'initial-value',
        `Fragment "${fragmentName}" does not support setting values (inputMode: "${config.inputMode}")`,
      );
      return;
    }

    logger.debug('initial-value', `Applying initial value for "${fragmentName}"`, values);
    this.setValues(fragmentName, values);
  }

  /**
   * Submit handler - validates all fragments are ready before processing
   *
   * @returns boolean
   */
  public submit(): boolean {
    if (this.dataState.attempts.isProcessing) {
      logger.warn('submit', 'Ignored — submission already in progress');
      return false;
    }

    if (!this.lifecycle.allFragmentsStaged()) {
      const notReady = this.lifecycle.getUnstaggedFragments();
      logger.warn('submit', `Blocked — fragments not ready: ${notReady.join(', ')}`, { notReady });

      const transactionStatus: TransactionStatusEventPayload = {
        status: 'error',
        message: `Fragments not ready ${notReady.join(', ')}`,
      };
      this.events.emit('transaction-status-change', transactionStatus);
      return false;
    }

    logger.time('submit', 'submission-round-trip');
    logger.info('submit', 'All fragments ready — beginning submission');

    this.dataState.attempts.create();
    logger.info(
      'submit',
      `Generated paymentAttemptId: ${this.dataState.attempts.paymentAttemptId}`,
    );

    const processable = this.fragmentRegistry.getProcessableFragments();

    logger.group(
      'submit-fragment',
      `Sending process command to ${processable.length} fragment(s)`,
      () => {
        processable.forEach(([, fragment]) => {
          if (fragment.status === 'staged') {
            logger.info('submit-fragment', `→ ${fragment.fragmentName} (${fragment.status})`);
            this.orchestrator.sendCommandToFragment(fragment, 'submit-fragment', null);
          } else {
            logger.debug(
              'submit-fragment',
              `↷ ${fragment.fragmentName} skipped (${fragment.status})`,
            );
          }
        });
      },
    );

    this.orchestrator.sendTransactionSubmitCommandToOrchestratorFragment();

    this.events.emit('submitted', null);
    this.orchestrator.sendStatusUpdateCommandToOrchestratorFragment();

    return true;
  }

  /**
   * Handle process request from a fragment.
   *
   * @param fragmentName - Name of the fragment
   * @returns void
   */
  public handleProcessRequest(fragmentName: string): void {
    logger.info('submit-fragment', `Submit trigger received from fragment "${fragmentName}"`);

    const accepted = this.submit();

    if (!accepted) {
      logger.warn('submit-fragment', `Submit rejected — triggered by "${fragmentName}"`);
      this.orchestrator.sendLogCommandToOrchestratorFragment(
        `Process request rejected from fragment: ${fragmentName}`,
      );
    }
  }

  /**
   * Handle assemble request.
   *
   * @param fragmentName - Name of the fragment
   * @returns void
   */
  public handleAssembleResponse(
    fragmentName: string,
    response: TransactionAssembleResponseMessagePayload,
  ): void {
    logger.info(
      'transaction-assemble-response',
      `Response from transaction-assemble recieved from ${fragmentName} fragment.`,
      response,
    );
    // TODO: handle response
  }

  /**
   * Handle process request from a fragment.
   *
   * @param fragmentName - Name of the fragment
   * @returns void
   */
  public handleProcessResponse(transactionStatus: TransactionStatusMessagePayload): void {
    logger.info(
      'transaction-status',
      `Transaction status returned from signalr-hub.`,
      transactionStatus,
    );

    const paymentAttemptUpdated = this.dataState.attempts.update(
      transactionStatus.status,
      transactionStatus.errorMessage,
    );

    if (paymentAttemptUpdated) {
      logger.info(
        'transaction-status',
        `Updated paymentAttemptLog for ${this.dataState.attempts.paymentAttemptId}`,
        transactionStatus,
      );
    } else {
      logger.warn(
        'transaction-status',
        `No paymentAttemptLog found for paymentAttemptId: ${this.dataState.attempts.paymentAttemptId}`,
      );
    }

    this.events.emit('transaction-status-change', { ...transactionStatus });
  }
}
